const Logger = require('@ffw/logger-lib');

// Leave JOB APP legacy method untouched.
/* eslint-disable camelcase */
/* eslint-disable sonarjs/cognitive-complexity */
// Usage is intentional, designed to mutate jobData object for dataLayerJobClick callback.
/* eslint-disable no-param-reassign */
const initDataLayerObject = (pageType, language) => {
  let env = process.env.NODE_ENV;
  switch (env) {
    case 'development':
      env = 'DEV';
      break;
    case 'test':
    case 'tst2':
      env = 'QA';
      break;
    case 'production':
    case 'prod':
    case 'prd2':
      env = 'PROD';
      break;
    default:
      env = 'DEV';
  }

  return {
    environment: env.toUpperCase(),
    country: process.env.REACT_APP_COUNTRY.toUpperCase(),
    type: pageType,
    language,
    search_results: {
      search_result_amount: 0,
      search_result_distance: 0,
      search_result_page: 0,
      search_result_zip_code: '',
      search_result_keyword: '',
    },
  };
};

// Helper that tries to resolve string to array changes of some vars.
// More specifically when viewing a job some of it's string data splitted to array.
// But that doesn't handle job impressions (i.e. related jobs data).
const handleBackwardsCompatibility = (data) => (typeof data === 'string' ? data : data.join());

// Helper for formatting numbers.
const formatNumber = (number) => {
  // Convert possible string value to number.
  number = parseFloat(number);
  // Fix it to 2 digits and again parse it to number.
  return parseFloat(number.toFixed(2));
};

const push = (data, key = false, subKey = false) => ((key !== false && subKey !== false)
  ? `window.dataLayer = window.dataLayer || []; window.dataLayer[${key}].${subKey} = data.${subKey};`
  : `window.dataLayer = window.dataLayer || []; let newData = ${JSON.stringify(data)}; window.dataLayer.push(newData);`);

/**
 * Helper to calculate annual salary.
 *
 * Note: All calculations done with following assumptions:
 *  - 8 working hours per day;
 *  - 5 working hours per week;
 *  - 52 weeks per year.
 *
 * @param {Number} value
 *   Salary value.
 * @param {'per hour'|'per day'|'per week'} compensationType
 *   Salary type.
 * @return {Number}
 *   Annual salary value.
 */
const getAnnualSalary = (value, compensationType) => {
  switch (compensationType) {
    case 'per hour':
      return value * 2080; // 8 h/day * 5 days/week * 52 week/year.
    case 'per day':
      return value * 260; // 5 days/week * 52 week/year.
    case 'per week':
      return value * 52; // 52 week/year.
    default:
      return value;
  }
};

/**
 * Helper function that returns datalayer object for a job.
 * Mutates job object and adds datalayer properties.
 *
 * @param {{}} job
 *   RUSA API Job object.
 * @param {Number} position
 *   The job position in the list.
 * @param {string} listName
 *   The job name of the list.
 * @param {string|false} applicationId
 *   Application ID.
 * @return {{}|false}
 */
const parseJobInfo = (job, position = 0, listName = '', applicationId = false) => {
  if (!job) {
    Logger.info('Missing job info when building datalayer.', 'dataLayerHelper');
    return false;
  }

  const info = {
    application_id: applicationId || '',
    application_type: 'direct application',
    job_title: job.BlueXJobData.Title || '',
    job_id: job.BlueXJobData.JobId || '',
    category: job.BlueXJobData.Specialism || '',
    operating_company: job.BlueXJobData.CompanyName || '',
    position: position || 0,
    list_name: listName || '',
    industry: job.JobInformation.Industry || '',
    subcategory: job.BlueXJobData ? job.BlueXJobData.SubSpecialism || '' : '',
    hours: job.JobInformation.Hours || '',
    minimum_salary: (job.Salary.SalaryMin && job.Salary.CompensationTypeId)
      ? formatNumber(getAnnualSalary(Number(job.Salary.SalaryMin), job.Salary.CompensationTypeId)) : 0,
    maximum_salary: (job.Salary.SalaryMax && job.Salary.CompensationTypeId)
      ? formatNumber(getAnnualSalary(Number(job.Salary.SalaryMax), job.Salary.CompensationTypeId)) : 0,
    employment_type: job.BlueXJobData.JobType || 0,
    work_experience: job.JobInformation.Experience ? handleBackwardsCompatibility(job.JobInformation.Experience) : '',
    education_level: job.JobInformation.Education ? handleBackwardsCompatibility(job.JobInformation.Education) : '',
    internal: job.JobInformation.JobInternal ? (job.JobInformation.JobInternal === 'true') : false,
    zip_code: job.JobLocation.Postcode || '',
    city: job.BlueXJobData.City || '',
    region: job.BlueXJobData.Region || '',
    // @TODO: Update these when they're available.
    business_unit: '',
    branch_region: '',
    branch_city: '',
    branch_zip_code: '',
    standardized_job_code: '',
    standardized_job_title: '',
    reference_number: job.JobId || '',
    branch_name: job.Contact.Office || '',
    skills: job.JobInformation.Skills ? handleBackwardsCompatibility(job.JobInformation.Skills) : '',
    url: `${process.env.REACT_APP_BASE_URL}${job.JobUrl}`,
  };

  // Add jobClick event.
  job.dataLayerJobClick = push({
    event: 'jobClick',
    ecommerce: {
      click: {
        list: listName,
        jobs: [info],
      },
    },
  });

  return info;
};

/**
 * Create datalayer object for GA4.
 */
export const dataLayerGA4Object = (jobTitle, location, grossSalary, averageSalary) => ({
  event: 'interaction',
  event_params: {
    event_name: 'salary_checker',
    job_title: jobTitle,
    search_location: location,
    annual_salary: grossSalary || 0,
    average_salary: averageSalary || 'no result',
  },
});

/* eslint-enable camelcase */
/* eslint-enable sonarjs/cognitive-complexity */
/* eslint-enable no-param-reassign */

/**
 * Returns Salary Page datalayer page object.
 *
 * @param {{}} data
 *   Page route data.
 * @param {string} locale
 *   App locale.
 * @return {{}}
 *   Object ready for usage with dataLayer.
 */
export const dataLayerSalaryCheckerPageView = (locale = process.env.REACT_APP_DEFAULT_LANGUAGE) => ({
  page: initDataLayerObject('salary_checker', locale),
});

/**
 * Data layer event for the related jobs block.
 */
export const pushDataLayerRelatedJobs = (jobs) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'jobImpressions',
    ecommerce: {
      impressions: jobs.map((job, index) => parseJobInfo(job, (index + 1), 'salary_checker')),
    },
  });
};

/**
 * Create datalayer object for.
 */
export const dataLayerObject = (jobTitle, location, grossSalary, averageSalary) => ({
  eventCategory: 'Salary Checker',
  eventAction: jobTitle,
  eventLabel: location,
  annualSalary: grossSalary || 0,
  averageSalary: averageSalary || 'no result',
  event: 'eventTracker',
});

/**
 * Data layer event for the average salary block.
 */
export const pushDataLayerSalaryInfo = (jobTitle, location, grossSalary, averageSalary) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(dataLayerObject(jobTitle, location, grossSalary, averageSalary));
  window.dataLayer.push({ event_params: null }); // eslint-disable-line
  window.dataLayer.push(dataLayerGA4Object(jobTitle, location, grossSalary, averageSalary));
};

/**
 * Data layer event for the job alert creation event.
 */
export const pushDataLayerAlertCreated = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    eventCategory: '[H] Candidate',
    eventAction: '[H] Soft Conversion',
    eventLabel: '[H] JobAlert created',
    event: 'eventTracker',
  });
};
